import React, { useRef, useState, useEffect } from "react";
import volIcon from "./asset/icons8-volume-24.png";
import fullScreenIcon from "./asset/icons8-expand-24.png";
import styles from "./VideoPlayer.module.css"; // Import CSS module
import forward from "./asset/icons8-forward-10-24.png";
import backward from "./asset/icons8-replay-10-24.png";

const VideoPlayer = ({ HndVideoEnd }) => {
  const videoRef = useRef(null);
  const progressRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [videoDuration, setVideoDuration] = useState(0);
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  const [hasEnded, setHasEnded] = useState(false);
  const [watchedTime, setWatchedTime] = useState(0);
  const [timerInterval, setTimerInterval] = useState(null);
  const [volume, setVolume] = useState(1);
  const [isVolumeVisible, setIsVolumeVisible] = useState(false);

  // Play video
  const playVideo = () => {
    videoRef.current.play();
    setIsPlaying(true);
    setHasEnded(false);

    // Start the timer when the video starts
    const interval = setInterval(() => {
      setWatchedTime((prevTime) => {
        const newTime = prevTime + 1;

        // If watchedTime reaches 50 seconds (or another threshold), call HndVideoEnd()
        if (newTime === 50) {
          setTimeout(() => {
            HndVideoEnd();
          }, 0);
        }

        return newTime;
      });
    }, 1000);
    setTimerInterval(interval);
  };

  // Pause video
  const pauseVideo = () => {
    videoRef.current.pause();
    setIsPlaying(false);

    // Stop the timer when the video is paused
    if (timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
    }

    console.log(`Video paused at ${watchedTime} seconds`);
  };

  // Stop Video (Pause + Reset Time)
  const stopVideo = () => {
    videoRef.current.pause();
    videoRef.current.currentTime = 0;
    setIsPlaying(false);
    setVideoCurrentTime(0);
    setWatchedTime(0);
    setHasEnded(false);

    if (timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
    }
  };

  // Update Progress Bar
  const updateProgress = () => {
    const currentTime = videoRef.current.currentTime;
    setVideoCurrentTime(currentTime);

    if (videoDuration) {
      const progress = (currentTime / videoDuration) * 100;
      if (progressRef.current) {
        progressRef.current.value = progress;
      }
    }
  };

  // Seek Video Time
  const handleProgressChange = (e) => {
    const value = e.target.value;
    const newTime = (value / 100) * videoDuration;
    videoRef.current.currentTime = newTime;
    setVideoCurrentTime(newTime);
  };

  // Get Video Metadata (Duration)
  const handleLoadedMetadata = () => {
    setVideoDuration(videoRef.current.duration);
  };

  // Adjust Volume
  const handleVolumeChange = (e) => {
    const volume = e.target.value;
    videoRef.current.volume = volume;
    setVolume(volume);
  };

  // Toggle Fullscreen Mode
  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      videoRef.current.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  // Toggle Volume Slider Visibility
  const toggleVolumeControl = () => {
    setIsVolumeVisible((prev) => !prev);
  };

  // Backward 10 seconds
  const seekBackward = () => {
    const newTime = videoRef.current.currentTime - 10;
    videoRef.current.currentTime = newTime >= 0 ? newTime : 0;
    setVideoCurrentTime(videoRef.current.currentTime);
  };

  // Forward 10 seconds
  const seekForward = () => {
    const newTime = videoRef.current.currentTime + 10;
    if (newTime <= videoDuration) {
      videoRef.current.currentTime = newTime;
      setVideoCurrentTime(newTime);
    }
  };

  // Listen for Video Events
  useEffect(() => {
    const video = videoRef.current;

    // Listen for timeupdate and video end events
    video.addEventListener("timeupdate", updateProgress);
    video.addEventListener("ended", () => {
      setHasEnded(true);
      // Optionally call the end handler directly
      HndVideoEnd();
    });

    return () => {
      video.removeEventListener("timeupdate", updateProgress);
      video.removeEventListener("ended", () => setHasEnded(true));

      if (timerInterval) {
        clearInterval(timerInterval);
      }
    };
  }, [videoDuration, timerInterval]);

  return (
    <div className={styles.container}>
      {/* Step 1 & Step 2 Container */}
      <div className={styles.stepsContainer}>
        <div className={styles.videoWrapper}>
          <video
            ref={videoRef}
            preload="metadata"
            onLoadedMetadata={handleLoadedMetadata}
            className={styles.video}
            onClick={isPlaying ? pauseVideo : playVideo} // Clicking on video toggles play/pause
          >
            <source src="/Tvideo.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>

          {/* Play/Pause Button in Center */}
          <button
            className={styles.playPauseOverlay}
            onClick={isPlaying ? pauseVideo : playVideo}
          >
            {isPlaying ? "⏸" : "▶"}
          </button>

          {/* Custom Controls */}
          <div className={styles.progressContainer}>
            <div className={styles.controls}>
              <button onClick={seekBackward} className={styles.seekButton}>
                <img src={backward} alt="Rewind 10 seconds" />
              </button>
              <button onClick={seekForward} className={styles.seekButton}>
                <img src={forward} alt="Forward 10 seconds" />
              </button>
            </div>

            {/* Progress Bar */}
            <input
              ref={progressRef}
              type="range"
              min="0"
              max="100"
              value={(videoCurrentTime / videoDuration) * 100}
              onChange={handleProgressChange}
              className={styles.progressBar}
            />

            {/* Volume Control */}
            <button onClick={toggleVolumeControl} className={styles.volumeButton}>
              <img src={volIcon} className={styles.icon} alt="Volume" />
            </button>

            {isVolumeVisible && (
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={volume}
                onChange={handleVolumeChange}
                className={styles.volumeControl}
              />
            )}

            {/* Fullscreen Button */}
            <button onClick={toggleFullScreen} className={styles.fullscreenButton}>
              <img src={fullScreenIcon} className={styles.icon} alt="Fullscreen" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;
